// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Passport reads itself (Phase 4)
// ==============================================
// The passport item's hook into the reader (Crew-4 "Read from the photo").
// Small on purpose: it is in the main bundle with CrewItemPage, and loads the
// client, its worker, Tesseract and the model (passportReaderClient.ts and on)
// only when a passport item opens with the reader switched on
// (isPassportReaderEnabled). Reading never blocks the form: the fields stay
// editable, Save works throughout, and nothing is uploaded until Save.
// ==============================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { isPassportReaderEnabled } from '../../lib/featureFlags';
import type { PassportDetails } from './passport';
import type { ReadOutcome } from './passportRead';
import type { PassportReaderClient } from './passportReaderClient';

export type ReadState =
  | { status: 'idle' }
  | { status: 'reading' }
  | { status: 'read'; message: string }
  | { status: 'partial'; message: string }
  | { status: 'failed'; message: string };

export const READ_MESSAGES = {
  read: 'Read from the photo — check them.',
  partial: 'Some details couldn’t be read — check these and type the rest.',
  failed: 'Couldn’t read the passport strip — type the details.',
  unavailable: 'This phone can’t read the passport strip — type the details.',
};

export function stateOf(o: ReadOutcome): ReadState {
  if (o.kind === 'read') return { status: 'read', message: READ_MESSAGES.read };
  if (o.kind === 'partial') return { status: 'partial', message: READ_MESSAGES.partial };
  return { status: 'failed', message: o.reason === 'unavailable' ? READ_MESSAGES.unavailable : READ_MESSAGES.failed };
}

export type LoadReader = () => Promise<PassportReaderClient>;
const loadReader: LoadReader = () => import('./passportReaderClient').then((m) => m.createPassportReaderClient());

export interface PassportReaderHook {
  enabled: boolean;
  state: ReadState;
  /** Read the photo on the phone; the details to prefill, or null. A later read or reset makes an earlier one return null. */
  read: (photo: Blob) => Promise<Partial<PassportDetails> | null>;
  reset: () => void;
}

export function usePassportReader(active: boolean, load: LoadReader = loadReader): PassportReaderHook {
  const [enabled] = useState(() => active && isPassportReaderEnabled());
  const [state, setState] = useState<ReadState>({ status: 'idle' });
  const client = useRef<Promise<PassportReaderClient | null> | null>(null);
  const run = useRef(0);

  // Start the reader as the screen opens, so the model loads while the photo is framed.
  useEffect(() => {
    if (!enabled) return undefined;
    const c = load().catch(() => null);
    client.current = c;
    return () => { run.current++; client.current = null; void c.then((x) => x?.dispose()); };
  }, [enabled, load]);

  const read = useCallback(async (photo: Blob) => {
    const pending = client.current;
    if (!pending) return null;
    const mine = ++run.current;
    setState({ status: 'reading' });
    let out: ReadOutcome;
    try {
      const c = await pending;
      out = c ? await c.read(photo) : { kind: 'none', reason: 'unavailable' };
    } catch { out = { kind: 'none', reason: 'error' }; }
    if (mine !== run.current) return null;
    setState(stateOf(out));
    return out.kind === 'none' ? null : out.details;
  }, []);

  const reset = useCallback(() => { run.current++; setState({ status: 'idle' }); }, []);
  return { enabled, state, read, reset };
}

/** The label over the passport fields. */
export function passportFieldsLabel(r: PassportReaderHook): string {
  return r.state.status === 'read' || r.state.status === 'partial' ? 'Read from the photo · check them' : 'The details on the photo page';
}

/** On the photo: "Reading…" while it reads, "Read" once it has. */
export function PassportReadBadge({ state }: { state: ReadState }) {
  if (state.status !== 'reading' && state.status !== 'read' && state.status !== 'partial') return null;
  const done = state.status !== 'reading';
  return (
    <span aria-hidden="true" className={`absolute right-3 top-3 flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[13px] font-semibold text-white ${done ? 'bg-[#0b7a3e]' : 'bg-[#16336b]'}`}>
      {done && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>}
      {state.status === 'reading' ? 'Reading…' : state.status === 'read' ? 'Read' : 'Partly read'}
    </span>
  );
}

/** Under the label: what the reader did, announced to screen readers. */
export function PassportReadNote({ state }: { state: ReadState }) {
  if (state.status === 'idle') return null;
  const text = state.status === 'reading' ? 'Reading the passport strip on your phone…' : state.message;
  const tone = state.status === 'read' ? 'text-[#6ee7a2]' : state.status === 'reading' ? 'text-[#c7d3e6]' : 'text-[#ffd48a]';
  return <p role="status" className={`m-0 text-[15px] font-semibold leading-snug ${tone}`}>{text}</p>;
}
